import { useQuery } from '@tanstack/react-query';
import { type ChangeEvent, useEffect, useState } from 'react';

import { api, type LeadFilters as Filters } from '../../api/endpoints';
import type { LeadStatus } from '../../api/types';
import { useDebounced } from '../../components/useDebounced';
import { geoStatusLabels } from '../../geo/labels';
import { useProject } from '../../projects/context';
import { projectKeys, useProjectApi } from '../../projects/useProjectApi';
import { hasActiveFilters, sortOptions } from './filters';

interface Props {
  filters: Filters;
  statuses: LeadStatus[];
  onChange: (patch: Partial<Filters>) => void;
  onReset: () => void;
  // mode: Makler-Sicht (B-28) – own = „Mit Termin“ (ohne Geo-, Regions-, Gebiets- und Zuständigkeitsfilter), pool = Regelgebiet
  // (Zuständigkeit und Gebiet stehen fest, Bundesland/Landkreis bleiben – der Bestand ist bundesweit).
  mode?: 'all' | 'own' | 'pool';
  // active: ob der Benutzer selbst gefiltert hat (feste Filter der Ansicht zählen nicht).
  active?: boolean;
}

export function LeadFilterBar({ filters, statuses, onChange, onReset, mode = 'all', active: userActive }: Props) {
  const own = mode !== 'all';
  const regions = mode !== 'own';
  const { can } = useProject();
  const papi = useProjectApi();
  const [search, setSearch] = useState(filters.q ?? '');
  const debounced = useDebounced(search.trim(), 300);
  const current = filters.q ?? '';

  // Erst übernehmen, wenn die Eingabe zur Ruhe gekommen ist (verhindert veraltete Werte nach "Zurücksetzen").
  useEffect(() => {
    if (debounced !== search.trim() || debounced === current || debounced.length === 1) return;
    onChange({ q: debounced || undefined });
  }, [debounced, search, current, onChange]);

  const states = useQuery({
    queryKey: ['geo', 'federal-states'],
    queryFn: ({ signal }) => api.federalStates(signal),
    staleTime: 60 * 60_000,
    enabled: can('geo.read'),
  });
  const counties = useQuery({
    queryKey: ['geo', 'counties', filters.federal_state_id],
    queryFn: ({ signal }) => api.counties({ federalStateId: filters.federal_state_id }, signal),
    staleTime: 60 * 60_000,
    enabled: can('geo.read') && !!filters.federal_state_id && filters.federal_state_id !== 'none',
  });
  const areas = useQuery({
    queryKey: [...projectKeys.areas(papi.projectId), 'active'],
    queryFn: ({ signal }) => papi.projectAreas(false, signal),
    enabled: can('area.read'),
  });

  const single = (key: keyof Filters) => (e: ChangeEvent<HTMLSelectElement | HTMLInputElement>) =>
    onChange({ [key]: e.target.value || undefined });
  const multi = (key: 'status' | 'geo_status') => (e: ChangeEvent<HTMLSelectElement>) =>
    onChange({ [key]: e.target.value ? [e.target.value] : undefined });
  const active = userActive ?? hasActiveFilters(filters);

  return (
    <form className="filters" role="search" aria-label="Leads filtern" onSubmit={(e) => e.preventDefault()}>
      <div className="filters-row">
        <label className="field grow">
          <span>Suche</span>
          <input
            type="search"
            value={search}
            placeholder="Name, Firma, Telefon, E-Mail, PLZ, Ort, Straße oder Lead-ID"
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <label className="field">
          <span>Status</span>
          <select value={filters.status?.[0] ?? ''} onChange={multi('status')}>
            <option value="">Alle</option>
            {statuses.map((s) => (
              <option key={s.code} value={s.code}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        {!own && (
          <label className="field">
            <span>Geo-Status</span>
            <select value={filters.geo_status?.[0] ?? ''} onChange={multi('geo_status')}>
              <option value="">Alle</option>
              {Object.entries(geoStatusLabels).map(([code, label]) => (
                <option key={code} value={code}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="field">
          <span>Sortierung</span>
          <select value={filters.sort ?? 'created_at'} onChange={(e) => onChange({ sort: e.target.value, order: undefined })}>
            {sortOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="filters-row">
        {can('geo.read') && regions && (
          <label className="field">
            <span>Bundesland</span>
            <select
              value={filters.federal_state_id ?? ''}
              onChange={(e) => onChange({ federal_state_id: e.target.value || undefined, county_id: undefined })}
            >
              <option value="">Alle</option>
              {(states.data?.items ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
              <option value="none">Ohne Zuordnung</option>
            </select>
          </label>
        )}
        {can('geo.read') && regions && (
          <label className="field">
            <span>Landkreis</span>
            <select value={filters.county_id ?? ''} onChange={single('county_id')}>
              <option value="">{filters.federal_state_id ? 'Alle' : 'Alle (Bundesland wählen)'}</option>
              {(counties.data?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.type_label})
                </option>
              ))}
              {filters.county_id && !counties.data?.items.some((c) => c.id === filters.county_id) && filters.county_id !== 'none' && (
                <option value={filters.county_id}>Ausgewählter Landkreis</option>
              )}
              <option value="none">Ohne Zuordnung</option>
            </select>
          </label>
        )}
        {can('area.read') && !own && (
          <label className="field">
            <span>Gebiet</span>
            <select value={filters.area_id ?? ''} onChange={single('area_id')}>
              <option value="">Alle</option>
              {(areas.data?.items ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
              <option value="none">Ohne Gebiet</option>
            </select>
          </label>
        )}
        <label className="field narrow">
          <span>PLZ</span>
          <input
            inputMode="numeric"
            maxLength={5}
            value={filters.postal_code ?? ''}
            placeholder="z. B. 701"
            onChange={(e) => onChange({ postal_code: e.target.value.replace(/\D/g, '') || undefined })}
          />
        </label>
        {!own && (
          <>
            <label className="field">
              <span>Zuständig</span>
              <select value={filters.assigned_user_id ?? ''} onChange={single('assigned_user_id')}>
                <option value="">Alle</option>
                <option value="me">Mir zugewiesen</option>
                <option value="none">Nicht zugewiesen</option>
              </select>
            </label>
            <label className="field">
              <span>Außendienst</span>
              <select value={filters.assigned_field_sales_user_id ?? ''} onChange={single('assigned_field_sales_user_id')}>
                <option value="">Alle</option>
                <option value="me">Mir zugewiesen</option>
                <option value="none">Ohne Außendienst</option>
              </select>
            </label>
          </>
        )}
      </div>
      <details className="filters-more">
        <summary>Weitere Filter</summary>
        <div className="filters-row">
          <label className="field">
            <span>Termin</span>
            <select value={filters.has_appointment ?? ''} onChange={single('has_appointment')}>
              <option value="">Alle</option>
              <option value="true">Mit Termin</option>
              <option value="false">Ohne Termin</option>
            </select>
          </label>
          <label className="field">
            <span>Wiedervorlage</span>
            <select value={filters.has_follow_up ?? ''} onChange={single('has_follow_up')}>
              <option value="">Alle</option>
              <option value="true">Mit Wiedervorlage</option>
              <option value="false">Ohne Wiedervorlage</option>
            </select>
          </label>
          <DateRange label="Nächster Kontakt" from="next_contact_from" to="next_contact_to" filters={filters} onChange={onChange} />
          <DateRange label="Letzter Kontakt" from="last_contact_from" to="last_contact_to" filters={filters} onChange={onChange} />
          <DateRange label="Angelegt" from="created_from" to="created_to" filters={filters} onChange={onChange} />
        </div>
      </details>
      {active && (
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => {
            setSearch('');
            onReset();
          }}
        >
          Filter zurücksetzen
        </button>
      )}
    </form>
  );
}

function DateRange({
  label,
  from,
  to,
  filters,
  onChange,
}: {
  label: string;
  from: keyof Filters;
  to: keyof Filters;
  filters: Filters;
  onChange: (patch: Partial<Filters>) => void;
}) {
  return (
    <fieldset className="field date-range">
      <legend>{label}</legend>
      <input
        type="date"
        aria-label={`${label} von`}
        value={(filters[from] as string | undefined) ?? ''}
        onChange={(e) => onChange({ [from]: e.target.value || undefined })}
      />
      <span aria-hidden="true">–</span>
      <input
        type="date"
        aria-label={`${label} bis`}
        value={(filters[to] as string | undefined) ?? ''}
        onChange={(e) => onChange({ [to]: e.target.value || undefined })}
      />
    </fieldset>
  );
}
